import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router-dom'

import {
  CircularProgress,
  List,
  ListItem,
  Stack,
  Typography,
} from '@mui/material'

import errorHandlerHook from '@hooks/useErrorHandler'

import BlogPostService from '@services/blog-post'

import { TLGTypography } from '@components/TLGTypography'

import LinkUtils from '@utils/links'
import { BlogPostType, IBlogPost } from '@utils/types'

import { listItemSx, listItemTextSx, listSx, loadingWrapperSx } from './styles'

export interface IRecentBlogPostsProps {
  postType: BlogPostType
}

const RecentBlogPosts = ({ postType }: IRecentBlogPostsProps) => {
  const { t } = useTranslation()
  const { handleError } = errorHandlerHook.useErrorHandler()

  const [isLoadingRecentBlogPosts, setIsLoadingRecentBlogPosts] =
    useState(false)
  const [recentBlogPosts, setRecentBlogPosts] = useState<IBlogPost[]>([])

  const fetchRecentBlogPosts = useCallback(async () => {
    setIsLoadingRecentBlogPosts(true)

    try {
      const { data } = await BlogPostService.findAllAsVisitor({
        postType,
        queryFilters: { page: 1, limit: 4 },
      })

      setRecentBlogPosts(data)
    } catch (err) {
      handleError({ exception: err })
    }

    setIsLoadingRecentBlogPosts(false)
  }, [handleError, postType])

  useEffect(() => {
    fetchRecentBlogPosts()
  }, [fetchRecentBlogPosts])

  const isRecentBlogPostsEmpty = recentBlogPosts.length === 0
  const getBlogPostUrlLink = ({ blogPost }: { blogPost: IBlogPost }) => {
    const { type, slug } = blogPost
    return `/blog/${type}/${slug}`
  }

  return (
    <Stack>
      <TLGTypography variant="h4" component="h2">
        {t('Recent Posts')}
      </TLGTypography>
      {isLoadingRecentBlogPosts ? (
        <Stack sx={loadingWrapperSx}>
          <CircularProgress />
        </Stack>
      ) : (
        <>
          {isRecentBlogPostsEmpty ? (
            <TLGTypography variant="h6" component="h3">
              {t('No recent blog posts found')}
            </TLGTypography>
          ) : (
            <List sx={listSx}>
              {recentBlogPosts.map((blogPost) => (
                <ListItem
                  component={RouterLink}
                  key={blogPost.id}
                  data-testid="recent-blog-post"
                  title={blogPost.title}
                  to={getBlogPostUrlLink({ blogPost })}
                  rel={LinkUtils.getLinkRel()}
                  sx={listItemSx}
                >
                  <Typography sx={listItemTextSx}>{blogPost.title}</Typography>
                </ListItem>
              ))}
            </List>
          )}
        </>
      )}
    </Stack>
  )
}

export { RecentBlogPosts }
